import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { client, OutboundTaskType, OutboundTaskTypeKey, TaskEvent, TaskComment } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { TaskContext } from '../../contexts';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import DeclineModal from '../../DeclineModal';
import { ActionCard } from '../../../ActionCard';
import { tasksQueryKey } from '../../../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

type ResponseValue = 'process_as_medical_benefits' | 'process_as_pharmacy_benefits';

// Static options - vary based on whether coverage was found
const OPTIONS_WITH_COVERAGE: { label: string; value: ResponseValue }[] = [
  { label: 'Proceed with PA under medical benefits', value: 'process_as_medical_benefits' },
  {
    label: 'Pursue coverage under pharmacy benefits to fulfill via specialty pharmacy',
    value: 'process_as_pharmacy_benefits',
  },
];

const OPTIONS_NO_COVERAGE: { label: string; value: ResponseValue }[] = [
  {
    label: 'Pursue coverage under pharmacy benefits to fulfill via specialty pharmacy',
    value: 'process_as_pharmacy_benefits',
  },
];

/**
 * Dedicated task view for benefits verification next steps (BENEFITS_VERIFICATION_NEXT_STEPS).
 */
const BenefitsVerificationNextStepsTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';

  const hasCoverage = task.has_coverage ?? false;
  const options = hasCoverage ? OPTIONS_WITH_COVERAGE : OPTIONS_NO_COVERAGE;

  const [selectedValue, setSelectedValue] = useState<ResponseValue>(options[0].value);

  const initialComment = task.timeline?.[0] as TaskComment | undefined;
  const lastTimelineItem =
    isCompleted && task.timeline && task.timeline.length > 1 ? task.timeline[task.timeline.length - 1] : undefined;
  const completionEvent = lastTimelineItem?.item_type === 'event' ? (lastTimelineItem as TaskEvent) : undefined;

  const handleTaskResponse = useMutation({
    mutationFn: async (response: ResponseValue) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking task response "${response}" for task ID: ${taskId}`);
        return;
      }
      const formData = new FormData();
      formData.append('task_response', response);
      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleSubmit = () => {
    handleTaskResponse.mutate(selectedValue);
  };

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        sidebar={{
          drug: { data: drug },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion event banner */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions from initial comment */}
        {!isCompleted && (
          <div className="mb-6 flex flex-col gap-4">
            <TaskInstructionSummary>Please select next step:</TaskInstructionSummary>
            {initialComment && initialComment.item_type === 'comment' && (
              <Typography styledAs="bodySmallDMSans">{initialComment.message}</Typography>
            )}
          </div>
        )}

        {/* Action card */}
        {!isCompleted && (
          <ActionCard>
            <RadioBlockGroup $variant="outlined">
              {options.map((option) => (
                <RadioOptionWrapper
                  key={option.value}
                  id={option.value}
                  name="bv-next-steps"
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => setSelectedValue(option.value)}
                  variant="outlined"
                  disabled={handleTaskResponse.isPending}
                >
                  <RadioLabel>{option.label}</RadioLabel>
                </RadioOptionWrapper>
              ))}
            </RadioBlockGroup>

            <ButtonRow align={Alignment.SpaceBetween} fullWidth>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setShowDeclineModal(true)}
                disabled={handleTaskResponse.isPending}
              >
                Cancel prescription
              </Button>
              <Button variant="primary" size="sm" onClick={handleSubmit} isLoading={handleTaskResponse.isPending}>
                Submit
              </Button>
            </ButtonRow>
          </ActionCard>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && patient && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}
    </>
  );
};

export default BenefitsVerificationNextStepsTaskView;
